import React, { useContext, useRef, useState } from "react";
import { Box, Button, Link, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { styled } from "@mui/material/styles";
import {
  LocationStatus,
  SupplierLocationDisplayVO
} from "../../../../types/LocationStatus";
import LocationCard from "../../card/LocationCard";
import EmailVerifyGuardTooltip from "../EmailVerifyGuardTooltip";
import { formatMoneyAddDecimalPointAndThousands } from "../../../../util/PaymentUtils";
import { submitLocationForReview, deactivateLocation, deleteLocation, cloneLocation, getActiveBookingsForLocation } from "../../requests/location-requests";
import {getErrorMessage} from "../../../../util/NetworkErrorUtil";
import SimpleConfirmationDialog from "../../../SimpleConfirmationDialog";
import AcknowledgmentDialog from "../../../AcknowledgmentDialog";
import { getLocationReviewCompletion } from "../../../../util/LocationCompletionUtil";
import CapacityManagerDialog from "../../capacity/CapacityManagerDialog";
import { useSnackbar } from "../../../../context/snackbar-context";
import { useFeatureFlags } from "../../../../hooks/routing/useFeatureFlags";
import moment from "moment";
import { AppContext } from "../../../../context/app-context";
import { hasAdminAccess } from "../../../../util/AccountUtils";
import { externalLinks } from "../../../../requests/external-endpoints";
import Busy from "../../../Busy";

interface LocationTabPanelProps {
  variant: LocationStatus;
  /** Locations already filtered to this panel's variant by the parent. */
  locations: SupplierLocationDisplayVO[];
  activeTab: LocationStatus;
  /** Called when the user requests to edit a location. */
  onEdit: (location: SupplierLocationDisplayVO) => void;
  /** Called when a location is successfully deactivated. */
  onDeactivateSuccess: (location: SupplierLocationDisplayVO) => void;
  /** Called when deactivation fails. */
  onDeactivateFailure: (errorMessage: string) => void;
  /** Called when a location is successfully deleted. */
  onDeleteSuccess: (location: SupplierLocationDisplayVO) => void;
  /** Called when deletion fails. */
  onDeleteFailure: (errorMessage: string) => void;
  /** Called when a draft location is successfully submitted for review. */
  onSubmitForReviewSuccess: (location: SupplierLocationDisplayVO) => void;
  /** Called when submitting for review fails. */
  onSubmitForReviewFailure: (errorMessage: string) => void;
  /** Called when a location is successfully cloned into a new draft. */
  onCloneSuccess: (location: SupplierLocationDisplayVO) => void;
  /** Called when cloning fails. */
  onCloneFailure: (errorMessage: string) => void;
  /** Whether the current user may clone locations (Account Owner / Admin only). */
  canClone: boolean;
  /** Called after a supplier saves/uploads capacity so the list can refresh. */
  onCapacityUpdated?: () => void;
  isEmailVerified: boolean;
}

/** Placeholder shown so a field cell keeps its slot when it has no value. */
const EMPTY_FIELD_VALUE = "—";

const EndOfListMessage = styled(Box)({
  textAlign: "center",
  padding: "18px 0",
});

const EndOfListText = styled(Typography)({
  fontSize: "0.8571rem",   // 12px at 14px root
  color: "#5E7782",
  fontWeight: 400,
});

/**
 * Renders the pre-filtered location list for the active tab variant.
 * Hidden (not unmounted) when another tab is active — preserves scroll position.
 */
const LocationTabPanel = ({
  variant,
  locations,
  activeTab,
  onEdit,
  onDeactivateSuccess,
  onDeactivateFailure,
  onDeleteSuccess,
  onDeleteFailure,
  onSubmitForReviewSuccess,
  onSubmitForReviewFailure,
  onCloneSuccess,
  onCloneFailure,
  canClone,
  onCapacityUpdated,
  isEmailVerified,
}: LocationTabPanelProps) => {
  const { user } = useContext(AppContext);
  const isAdmin = hasAdminAccess(user);
  // Tracks the id of the location currently being submitted for review,
  // to disable the button and prevent duplicate requests.
  const [submittingReviewId, setSubmittingReviewId] = useState<string | null>(null);
  // Synchronous ref used to block duplicate submit-for-review requests in the same
  // render cycle. State updates are async, so submittingReviewId alone cannot prevent
  // a rapid double-click from firing twice before React re-renders.
  const submittingReviewInFlightRef = useRef(false);
  // Tracks the id of the location currently being reactivated (same API as submit for review).
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  // Synchronous ref used to block duplicate reactivation requests in the same
  // render cycle. State updates are async, so reactivatingId alone cannot prevent
  // a rapid double-click from firing twice before React re-renders.
  const reactivatingInFlightRef = useRef(false);
  // The location awaiting reactivation confirmation; null when the dialog is closed.
  const [locationPendingReactivation, setLocationPendingReactivation] = useState<SupplierLocationDisplayVO | null>(null);
  // Tracks the id of the location currently being deactivated.
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);
  // The location awaiting deactivation confirmation; null when the dialog is closed.
  const [locationPendingDeactivation, setLocationPendingDeactivation] = useState<SupplierLocationDisplayVO | null>(null);
  // The location a supplier tried to deactivate while it still has active bookings.
  // Shows a blocking "Cannot Deactivate" dialog (no override); null when closed.
  const [locationBlockedDeactivation, setLocationBlockedDeactivation] = useState<SupplierLocationDisplayVO | null>(null);
  // The location an admin tried to deactivate while it still has active bookings.
  // Shows a warning dialog that lets the admin override and proceed; null when closed.
  const [locationAdminWarnDeactivation, setLocationAdminWarnDeactivation] = useState<SupplierLocationDisplayVO | null>(null);
  // Guards the active-bookings pre-check from firing twice on a rapid double-click.
  const deactivateCheckInFlightRef = useRef(false);
  // Tracks the id of the location currently being deleted.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // The location awaiting deletion confirmation; null when the dialog is closed.
  const [locationPendingDeletion, setLocationPendingDeletion] = useState<SupplierLocationDisplayVO | null>(null);
  // Tracks the id of the location currently being cloned.
  const [cloningId, setCloningId] = useState<string | null>(null);
  // Synchronous ref used to block duplicate clone requests in the same render
  // cycle. State updates are async, so cloningId alone cannot prevent a rapid
  // double-click from firing twice before React re-renders.
  const cloningInFlightRef = useRef(false);
  // The location awaiting clone confirmation; null when the dialog is closed.
  const [locationPendingClone, setLocationPendingClone] = useState<SupplierLocationDisplayVO | null>(null);
  // POC: the location whose capacity manager dialog is open; null when closed.
  const [capacityLocation, setCapacityLocation] = useState<SupplierLocationDisplayVO | null>(null);
  // Tracks whether any capacity save/upload happened this session, so the list is
  // refreshed once when the dialog closes rather than on every save (avoids the
  // global busy overlay flashing repeatedly while the supplier keeps editing).
  const capacityChangedRef = useRef(false);
  const { snackbarShowMessage } = useSnackbar();
  const { supplierCapacitySchedule } = useFeatureFlags();

  const handleSubmitForReview = async (location: SupplierLocationDisplayVO) => {
    // Synchronous guard: blocks a rapid double-click from firing two POSTs
    // before the async setSubmittingReviewId state update is committed.
    if (submittingReviewInFlightRef.current) return;
    submittingReviewInFlightRef.current = true;
    setSubmittingReviewId(location.locationId);
    try {
      const response = await submitLocationForReview(location.locationId);
      onSubmitForReviewSuccess(response.body as SupplierLocationDisplayVO);
    } catch (err) {
      onSubmitForReviewFailure(getErrorMessage(err));
    } finally {
      setSubmittingReviewId(null);
      submittingReviewInFlightRef.current = false;
    }
  };

  const handleRequestReactivation = async (location: SupplierLocationDisplayVO) => {
    setReactivatingId(location.locationId);
    try {
      const response = await submitLocationForReview(location.locationId);
      onSubmitForReviewSuccess(response.body as SupplierLocationDisplayVO);
    } catch (err) {
      onSubmitForReviewFailure(getErrorMessage(err));
    } finally {
      setReactivatingId(null);
      reactivatingInFlightRef.current = false;
    }
  };

  // Closes the confirmation dialog, then runs the reactivation request.
  // Guards against duplicate invocations: the synchronous ref blocks same-tick
  // double-clicks before React has committed the reactivatingId state update.
  const handleConfirmReactivation = () => {
    if (reactivatingInFlightRef.current) return;
    reactivatingInFlightRef.current = true;
    const location = locationPendingReactivation;
    setLocationPendingReactivation(null);
    if (location) {
      void handleRequestReactivation(location);
    } else {
      reactivatingInFlightRef.current = false;
    }
  };

  const handleDeactivate = async (location: SupplierLocationDisplayVO) => {
    setDeactivatingId(location.locationId);
    try {
      const response = await deactivateLocation(location.locationId);
      onDeactivateSuccess(response.body as SupplierLocationDisplayVO);
    } catch (err) {
      onDeactivateFailure(getErrorMessage(err, 'Failed to deactivate location.'));
    } finally {
      setDeactivatingId(null);
    }
  };

  // Starts the deactivation flow. Mirrors the legacy LocationListing behavior:
  // first check whether the location still has active bookings, and only offer
  // the plain deactivate confirmation when it has none. With active bookings a
  // supplier is blocked (must cancel/inactivate bookings first) while an admin
  // gets a warning they can override.
  const startDeactivate = async (location: SupplierLocationDisplayVO) => {
    if (deactivateCheckInFlightRef.current) return;
    deactivateCheckInFlightRef.current = true;
    setDeactivatingId(location.locationId);
    Busy.set(true);
    try {
      const res = await getActiveBookingsForLocation(location.locationId);
      const activeBookings = res.body?.count ?? 0;
      if (activeBookings > 0) {
        if (isAdmin) {
          setLocationAdminWarnDeactivation(location);
        } else {
          setLocationBlockedDeactivation(location);
        }
      } else {
        setLocationPendingDeactivation(location);
      }
    } catch (err) {
      onDeactivateFailure(getErrorMessage(err, 'Unable to verify whether this location has active bookings. Please try again.'));
    } finally {
      Busy.set(false);
      setDeactivatingId(null);
      deactivateCheckInFlightRef.current = false;
    }
  };

  // Closes the confirmation dialog, then runs the deactivation request.
  const handleConfirmDeactivation = () => {
    const location = locationPendingDeactivation;
    setLocationPendingDeactivation(null);
    if (location) {
      void handleDeactivate(location);
    }
  };

  // Admin override: closes the active-bookings warning and proceeds to deactivate.
  const handleConfirmAdminDeactivation = () => {
    const location = locationAdminWarnDeactivation;
    setLocationAdminWarnDeactivation(null);
    if (location) {
      void handleDeactivate(location);
    }
  };

  const handleDelete = async (location: SupplierLocationDisplayVO) => {
    setDeletingId(location.locationId);
    try {
      await deleteLocation(location.locationId);
      onDeleteSuccess(location);
    } catch (err) {
      onDeleteFailure(getErrorMessage(err, 'Failed to delete location.'));
    } finally {
      setDeletingId(null);
    }
  };

  // Closes the confirmation dialog, then runs the deletion request.
  const handleConfirmDeletion = () => {
    const location = locationPendingDeletion;
    setLocationPendingDeletion(null);
    if (location) {
      void handleDelete(location);
    }
  };

  const handleClone = async (location: SupplierLocationDisplayVO) => {
    setCloningId(location.locationId);
    try {
      const response = await cloneLocation(location.locationId);
      onCloneSuccess(response.body as SupplierLocationDisplayVO);
    } catch (err) {
      onCloneFailure(getErrorMessage(err, 'Please try again.'));
    } finally {
      setCloningId(null);
      cloningInFlightRef.current = false;
    }
  };

  // Closes the confirmation dialog, then runs the clone request.
  // Guards against duplicate invocations: the synchronous ref blocks same-tick
  // double-clicks before React has committed the cloningId state update.
  const handleConfirmClone = () => {
    if (cloningInFlightRef.current) return;
    cloningInFlightRef.current = true;
    const location = locationPendingClone;
    setLocationPendingClone(null);
    if (location) {
      void handleClone(location);
    } else {
      cloningInFlightRef.current = false;
    }
  };

  // Clone is gated to Account Owners/Admins and disabled (with a tooltip) until
  // the user has verified their email. Rendered on every card variant.
  const renderCloneButton = (location: SupplierLocationDisplayVO) => {
    if (!canClone) return null;
    return (
      <EmailVerifyGuardTooltip
        disabled={!isEmailVerified}
        disabledReason="You must confirm your email address before cloning a location."
      >
        <Button variant="outlined" color="inherit" size="small"
          disabled={cloningId !== null}
          onClick={() => setLocationPendingClone(location)}>
          Clone
        </Button>
      </EmailVerifyGuardTooltip>
    );
  };

  return (
    <Box role="tabpanel" hidden={activeTab !== variant} aria-label={`${variant} locations`}>
      {locations.map((location: SupplierLocationDisplayVO, index: number) => {
        const address = [location.addressLine1, location.city, location.state]
          .filter(Boolean)
          .join(", ");
        const draftCompletion = getLocationReviewCompletion(location);

        return (
          <LocationCard key={location.locationId ?? index} derivedStatus={location.derivedStatus}>
            <LocationCard.Image fileName={location.listingImageFileName} alt={location.locationName} />
            <LocationCard.Content>

              {/* Header row: name + address stacked left, actions right */}
              <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <LocationCard.Header
                  name={location.locationName}
                  address={address}
                  phone={location.phoneNumber}
                  // Only active locations have a public listing page.
                  href={location.derivedStatus === "active" ? `/location-profile/${location.locationId}` : undefined}
                />
                <LocationCard.Actions>
                  {/* Active: Edit + Deactivate */}
                  {location.derivedStatus === "active" && (
                    <>
                      <EmailVerifyGuardTooltip disabled={!isEmailVerified}>
                        <Button variant="contained" color="success" size="small" sx={{ color: "#fff" }}
                          onClick={() => onEdit(location)}>
                          Edit
                        </Button>
                      </EmailVerifyGuardTooltip>
                      {supplierCapacitySchedule &&
                        (location.managedLocation ? (
                          <Button variant="outlined" color="primary" size="small"
                            onClick={() => setCapacityLocation(location)}>
                            Capacity
                          </Button>
                        ) : (
                          <Tooltip
                            title="Enable automated approvals for this location to manage capacity."
                            arrow
                            slotProps={{
                              tooltip: {
                                sx: { textAlign: "center", maxWidth: 200, fontSize: "0.8rem" },
                              },
                            }}
                          >
                            <span style={{ display: "inline-flex" }}>
                              <Button variant="outlined" color="primary" size="small"
                                disabled
                                onClick={() => setCapacityLocation(location)}>
                                Capacity
                              </Button>
                            </span>
                          </Tooltip>
                        ))}
                      <Button variant="outlined" color="error" size="small"
                        disabled={deactivatingId === location.locationId}
                        onClick={() => startDeactivate(location)}>
                        Deactivate
                      </Button>
                      {renderCloneButton(location)}
                    </>
                  )}
                  {/* Pending: Edit only */}
                  {location.derivedStatus === "pending" && (
                    <>
                      <EmailVerifyGuardTooltip disabled={!isEmailVerified}>
                        <Button variant="contained" color="success" size="small" sx={{ color: "#fff" }}
                          onClick={() => onEdit(location)}>
                          Edit
                        </Button>
                      </EmailVerifyGuardTooltip>
                      {renderCloneButton(location)}
                    </>
                  )}
                  {/* Draft: Delete + Edit + Submit for Review */}
                  {location.derivedStatus === "draft" && (
                    <>
                      <Button variant="outlined" color="inherit" size="small"
                        disabled={deletingId === location.locationId}
                        onClick={() => setLocationPendingDeletion(location)}>
                        Delete
                      </Button>
                      <EmailVerifyGuardTooltip disabled={!isEmailVerified}>
                        <Button variant="contained" color="success" size="small" sx={{ color: "#fff" }}
                          onClick={() => onEdit(location)}>
                          Edit
                        </Button>
                      </EmailVerifyGuardTooltip>
                      <Tooltip
                        title={
                          !draftCompletion.isComplete
                            ? "Complete all required fields before submitting for review. See the missing fields listed below."
                            : ""
                        }
                        arrow
                        disableHoverListener={draftCompletion.isComplete}
                        disableFocusListener={draftCompletion.isComplete}
                      >
                        {/* tabIndex makes the span itself focusable (disabled buttons can't receive
                            focus), so keyboard users can tab to it and trigger the tooltip */}
                        <span
                          style={{ display: "inline-flex" }}
                          tabIndex={!draftCompletion.isComplete ? 0 : -1}
                        >
                          <Button variant="contained" color="warning" size="small" sx={{ color: "#fff" }}
                            disabled={!draftCompletion.isComplete || submittingReviewId === location.locationId}
                            aria-disabled={!draftCompletion.isComplete || submittingReviewId === location.locationId}
                            onClick={() => handleSubmitForReview(location)}>
                            Submit for Review
                          </Button>
                        </span>
                      </Tooltip>
                      {renderCloneButton(location)}
                    </>
                  )}
                  {/* Deactivated: Delete + Edit + Request Reactivation */}
                  {location.derivedStatus === "deactivated" && (
                    <>
                      <Button variant="outlined" color="inherit" size="small"
                        disabled={deletingId === location.locationId}
                        onClick={() => setLocationPendingDeletion(location)}>
                        Delete
                      </Button>
                      <EmailVerifyGuardTooltip disabled={!isEmailVerified}>
                        <Button variant="contained" color="success" size="small" sx={{ color: "#fff" }}
                          onClick={() => onEdit(location)}>
                          Edit
                        </Button>
                      </EmailVerifyGuardTooltip>
                      <Button variant="outlined" color="primary" size="small"
                        disabled={reactivatingId !== null}
                        onClick={() => setLocationPendingReactivation(location)}>
                        Request Reactivation
                      </Button>
                      {renderCloneButton(location)}
                    </>
                  )}
                </LocationCard.Actions>
              </Box>

              {/* Status-specific content */}
              {location.derivedStatus === "pending" && (
                // TODO: replace lastModifiedDate with the real submission timestamp
                // once the backend exposes it.
                <LocationCard.PendingNotice submittedDate={location.lastModifiedDate ?? undefined} />
              )}
              {location.derivedStatus === "draft" && (
                <LocationCard.DraftProgress
                  completionPercent={draftCompletion.completionPercent}
                  missingFields={
                    draftCompletion.missingFields.length
                      ? draftCompletion.missingFields.join(", ")
                      : undefined
                  }
                />
              )}

              {/* Fields grid */}
              <LocationCard.Fields>
                <LocationCard.Field label="Search Status">
                  <LocationCard.SearchStatusChip
                    isVisible={location.isVisible}
                    subLabel={
                      location.derivedStatus === "pending" ? "Pending Approval" :
                      // A draft is not visible until it's submitted and approved.
                      // Only call out "Missing Required Fields" when the listing
                      // is actually incomplete; a fully-completed draft is simply
                      // awaiting submission, so don't imply a validation problem.
                      // Deliberately not "Ready to Submit": this check covers blank
                      // required fields, not the wizard's validity rules.
                      location.derivedStatus === "draft"   ? (draftCompletion.isComplete ? "All required fields complete" : "Missing Required Fields") :
                      // TODO: use the real deactivation timestamp once available.
                      location.derivedStatus === "deactivated" ? `Deactivated on ${location.lastModifiedDate ?? "—"}` :
                      undefined
                    }
                  />
                </LocationCard.Field>
                {/* Every field below renders unconditionally so each card keeps the
                    same height and column layout regardless of which values are set. */}
                <LocationCard.Field label="Location Capacity">
                  {location.totalNumberOfSpaces ?? EMPTY_FIELD_VALUE}
                </LocationCard.Field>
                <LocationCard.Field label="Auto-Approve Spaces">
                  {/* Off shows a dash; the Automated Management cell already says "No". */}
                  {location.managedLocation
                    ? location.managedSpaces ?? EMPTY_FIELD_VALUE
                    : EMPTY_FIELD_VALUE}
                </LocationCard.Field>
                {supplierCapacitySchedule && location.derivedStatus === "active" && (
                  <LocationCard.Field label="Capacity Updated">
                    {location.capacityUpdatedAt
                      ? moment(location.capacityUpdatedAt).fromNow()
                      : "Never"}
                  </LocationCard.Field>
                )}
                <LocationCard.Field label="Per Day">
                  {location.pricePerDay == null
                    ? EMPTY_FIELD_VALUE
                    : location.pricePerDay > 0
                      ? formatMoneyAddDecimalPointAndThousands(location.pricePerDay)
                      : "Disabled"}
                </LocationCard.Field>
                <LocationCard.Field label="Per Month">
                  {location.pricePerMonth == null
                    ? EMPTY_FIELD_VALUE
                    : location.pricePerMonth > 0
                      ? formatMoneyAddDecimalPointAndThousands(location.pricePerMonth)
                      : "Disabled"}
                </LocationCard.Field>
                <LocationCard.Field label="Charge Overages">
                  {location.chargeOverages == null
                    ? EMPTY_FIELD_VALUE
                    : location.chargeOverages ? "Yes" : "No"}
                </LocationCard.Field>
                <LocationCard.Field label="Overage Rate">
                  {location.overageRate == null
                    ? EMPTY_FIELD_VALUE
                    : formatMoneyAddDecimalPointAndThousands(location.overageRate)}
                </LocationCard.Field>
                <LocationCard.Field label="Automated Management">
                  {location.managedLocation == null
                    ? EMPTY_FIELD_VALUE
                    : location.managedLocation ? "Yes" : "No"}
                </LocationCard.Field>
                <LocationCard.Field label="QR Scanner Available">
                  {location.qrScannerAvailable == null
                    ? EMPTY_FIELD_VALUE
                    : location.qrScannerAvailable ? "Yes" : "No"}
                </LocationCard.Field>
              </LocationCard.Fields>

            </LocationCard.Content>
          </LocationCard>
        );
      })}
      <EndOfListMessage>
        <EndOfListText>You have reached the end of the list</EndOfListText>
      </EndOfListMessage>
      <SimpleConfirmationDialog
        open={locationPendingReactivation !== null}
        title="Request Reactivation"
        body={
          `Are you sure you want to request reactivation for ${locationPendingReactivation?.locationName ?? "this location"}? ` +
          "It will be submitted for SecurSpace admin review before going live again."
        }
        onClose={() => setLocationPendingReactivation(null)}
        onConfirm={handleConfirmReactivation}
      />
      <SimpleConfirmationDialog
        open={locationPendingDeactivation !== null}
        title="Deactivate Location"
        body={
          `Are you sure you want to deactivate ${locationPendingDeactivation?.locationName ?? "this location"}? ` +
          "It will no longer be visible or searchable to buyers."
        }
        onClose={() => setLocationPendingDeactivation(null)}
        onConfirm={handleConfirmDeactivation}
      />
      {/* Supplier tried to deactivate a location that still has active bookings —
          blocked (no override). There's nothing to confirm here, just acknowledge. */}
      <AcknowledgmentDialog
        open={locationBlockedDeactivation !== null}
        title="Cannot Deactivate Location"
        body={
          <>
            <Typography variant={'body1'} sx={{ mb: 2 }}>
              All bookings must be cancelled or inactive before this location can be deactivated.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to={`/bookings/supplier-bookings?status=ACTIVE&locationId=${locationBlockedDeactivation?.locationId ?? ""}`}
              onClick={() => setLocationBlockedDeactivation(null)}
              sx={{ mb: 3 }}
            >
              View Active Bookings
            </Button>
            <Typography variant={'body2'}>
              Need help?{" "}
              <Link href={externalLinks.contact} target="_blank" rel="noopener noreferrer">
                Contact support by clicking here
              </Link>
              .
            </Typography>
          </>
        }
        onClose={() => setLocationBlockedDeactivation(null)}
      />
      {/* Admin tried to deactivate a location that still has active bookings —
          warned, but allowed to override, matching the legacy admin warning. */}
      <SimpleConfirmationDialog
        open={locationAdminWarnDeactivation !== null}
        title="Warning: Active Bookings Exist"
        body={
          <>
            <Typography sx={{ mb: 1.5 }}>
              This location has active bookings. All bookings should be cancelled or inactivated before deactivating this location.
            </Typography>
            <Typography sx={{ mb: 1.5 }}>
              <Link
                component={RouterLink}
                to={`/bookings/supplier-bookings?status=ACTIVE&locationId=${locationAdminWarnDeactivation?.locationId ?? ""}`}
              >
                View all active bookings for this location
              </Link>
            </Typography>
            <Typography>
              As an admin, you may override and proceed with deactivation.
            </Typography>
          </>
        }
        onClose={() => setLocationAdminWarnDeactivation(null)}
        onConfirm={handleConfirmAdminDeactivation}
      />
      <SimpleConfirmationDialog
        open={locationPendingDeletion !== null}
        title="Delete Location"
        body={
          `Are you sure you want to delete ${locationPendingDeletion?.locationName ?? "this location"}? ` +
          "This action is permanent and cannot be undone."
        }
        onClose={() => setLocationPendingDeletion(null)}
        onConfirm={handleConfirmDeletion}
      />
      <SimpleConfirmationDialog
        open={locationPendingClone !== null}
        title="Clone Location"
        body={
          `This will create a new Draft copy of "Copy-${locationPendingClone?.locationName ?? "this location"}". ` +
          "Do you want to continue?"
        }
        onClose={() => setLocationPendingClone(null)}
        onConfirm={handleConfirmClone}
      />
      {capacityLocation && (
        <CapacityManagerDialog
          open={capacityLocation !== null}
          locationId={capacityLocation.locationId}
          locationName={capacityLocation.locationName ?? "this location"}
          defaultCapacity={capacityLocation.totalNumberOfSpaces ?? null}
          autoApproveSpaces={capacityLocation.managedSpaces ?? null}
          managedLocation={Boolean(capacityLocation.managedLocation)}
          onClose={() => {
            setCapacityLocation(null);
            // Refresh the card's "Capacity Updated" once, after editing is done.
            if (capacityChangedRef.current) {
              capacityChangedRef.current = false;
              onCapacityUpdated?.();
            }
          }}
          onSaved={(message) => {
            snackbarShowMessage(message, "success");
            capacityChangedRef.current = true;
          }}
          onError={(message) => snackbarShowMessage(message, "error")}
        />
      )}
    </Box>
  );
};

export default LocationTabPanel;
